import React, { useState, useRef, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import calendarIcon from '@images/icons/calendar.svg';
import moment from 'moment';
import clockIcon from '@images/icons/clock.svg';
import TimeKeeper from 'react-timekeeper';
import styles from '../HomepageNotification.module.scss';
import generalStyles from '@/components/General/Styles.module.scss';
import Select from 'react-select';
import * as Constant from '../Constants';
import API_STATES from '@/constants/StateConstants';
import * as CompanyApi from '@/services/api/company.service';
import * as MessageService from '@/services/api/message.service';
import Popup from 'reactjs-popup';
import 'reactjs-popup/dist/index.css';
import { getMessageTypeValue } from '../Utils';
import { handleApiError } from '@/components/General/HandleApiError';
import { ByzzerWindowedSelect } from "@byzzer/ui-components";

function UserBasedNotificationForm({
    refresh,
    selectedNotification,
    edit = false,
    editSingleNotification = false,
    closeEdit,
}) {
    const [companyList, setCompanyList] = useState([]);
    const [userList, setUserList] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [endDate, setEndDate] = useState(edit ? moment(new Date(selectedNotification.endDate)) : null);
    const [selectedNotificationType, setSelectedNotificationType] = useState(
        edit ? getMessageTypeValue(selectedNotification.notificationType) : []
    );
    const [notificationMessage, setNotificationMessage] = useState(edit ? selectedNotification.message : '');
    const [notificationHeader, setNotificationHeader] = useState(edit ? selectedNotification.header : '');
    const [notificationLink, setNotificationLink] = useState(edit ? selectedNotification.hyperLink : '');
    const [companyApiState, setCompanyApiState] = useState(API_STATES.none);
    const [userApiState, setUserApiState] = useState(API_STATES.none);
    const [companyApiError, setCompanyApiError] = useState('');
    const [userApiError, setUserApiError] = useState('');
    const [isCompanyListChanged, setIsCompanyListChanged] = useState(false);
    const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
    const [createApiState, setCreateApiState] = useState(API_STATES.none);
    const [createApiError, setCreateApiError] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [openScheduleNotificationModal, setOpenScheduleNotificationModal] = useState(false)
    const [skippedCompanies, setSkippedCompanies] = useState([]);
    const [skippedUsers, setSkippedUsers] = useState([]);

    const [scheduledDate, setScheduledDate] = useState(null);
    const [time, setTime] = useState(moment()); // buffer to store new eta

    const [showTimePicker, setShowTimePicker] = useState(false);

    const [updateScheduledDateApiState, setUpdateScheduledDateApiState] = useState(API_STATES.none);
    const [updateScheduledDateApiError, setScheduledDateApiError] = useState('');

    const [removeScheduledDateApiState, setRemoveScheduledDateApiState] = useState(API_STATES.none);
    const [removeScheduledDateApiError, setRemoveScheduledDateApiError] = useState('');

    const loadingCompanyListMessage = companyApiState === API_STATES.loading ? 'Loading options...' : undefined;
    const loadingUserListMessage = userApiState === API_STATES.loading ? 'Loading options...' : undefined;

    const selectedUsersCount = selectedUsers[0] === Constant.ALL ? userList.length : selectedUsers.length;

    const transformCompanies = (list) => {
        return (
            list?.map((company) => {
                return { display: company.name, value: company.id };
            }) ?? []
        );
    };

    const fetchCompanyList = async () => {
        try {
            setCompanyApiState(companyApiState === API_STATES.success ? API_STATES.refreshing : API_STATES.loading);

            let response = await CompanyApi.getAllCompanies();
            if (response.status === API_STATES.success) {
                const companies = response.data;
                setCompanyList(transformCompanies(companies));
                setCompanyApiState(API_STATES.success);
            } else {
                setCompanyApiError(response.error);
                setCompanyList([]);
                setCompanyApiState(API_STATES.error);
            }
            setUserList([]);
            setSelectedUsers([]);
            setSkippedUsers([]);
        } catch (e) {
            setCompanyApiError(e);
            setCompanyList([]);
            setCompanyApiState(API_STATES.error);
        }
    };

    if (companyList.length === 0 && companyApiState === API_STATES.none) {
        fetchCompanyList();
    }

    const selectCompany = useCallback((selectedValues) => {
        let companyIds;
        if (selectedValues.some((company) => company === Constant.ALL)) {
            companyIds = companyList.map((obj) => obj.value);
            setSelectedCompany([Constant.ALL]);
        } else {
            companyIds = selectedValues.map((val) => val);
            setSelectedCompany(selectedValues);
            if (selectedValues?.length === 0) {
                setSkippedCompanies([]);
            }
        }
        setSelectedCompanyIds(companyIds);
        setIsCompanyListChanged(true);
        setSelectedUsers([]);
        setSkippedUsers([]);
        setUserApiState(API_STATES.none);
    }, [companyList]);

    const selectUsers = useCallback((selectedValues) => {
        if (selectedValues.some((user) => user === Constant.ALL)) {
            setSelectedUsers([Constant.ALL]);
        } else {
            setSelectedUsers(selectedValues);
            if (selectedValues?.length === 0) {
                setSkippedUsers([]);
            }
        }
    }, []);

    const onDateChange = (endDateValue) => {
        if (endDateValue && endDateValue !== '') setEndDate(endDateValue);
        else setEndDate(null);
    };

    const handleSelectedNotificationType = (selectedValue) => {
        setSelectedNotificationType(selectedValue);
    };

    const handleMessageChange = (e) => {
        setNotificationMessage(e.target.value);
    };

    const handleHeaderChange = (e) => {
        setNotificationHeader(e.target.value);
    };

    const handleLinkChange = (e) => {
        setNotificationLink(e.target.value);
    };

    const transformUsers = (list) => {
        return (
            list?.map((user) => {
                return {
                    display: user.first_name + ' ' + user.last_name + ' (' + user.email + ')',
                    value: { id: user.id, email: user.email },
                };
            }) ?? []
        );
    };

    // const userInputChange = (inputValue, actionMeta) => {
    //     if (actionMeta?.action === 'input-change' && userList?.length !== 0 && inputValue.indexOf('|') !== -1) {
    //         const users = inputValue.split('|');
    //         let res = userList.filter((item) => users.includes(item.label));
    //         let userNames = res.map(({ label }) => label);
    //         let skippedValues = users.filter((item) => !userNames.includes(item));

    //         if (res?.length !== 0) {
    //             let list = selectedUsers;
    //             for (let value of res) {
    //                 if (!selectedUsers?.includes(value)) {
    //                     list.push(value);
    //                 }
    //             }
    //             if (list?.length !== 0) {
    //                 selectUsers(list);
    //             }
    //             let skippedUsersList = skippedUsers;
    //             for (let value of skippedValues) {
    //                 if (!skippedUsers?.includes(value)) {
    //                     skippedUsersList.push(value);
    //                 }
    //             }
    //             if (skippedUsersList?.length !== 0) {
    //                 setSkippedUsers(skippedUsersList);
    //             }
    //         }
    //     }
    // };

    const userSelectClicked = async () => {
        try {
            if (selectedCompany && selectedCompany.length > 0 && isCompanyListChanged) {
                setUserList([]);
                setIsCompanyListChanged(false);
                setUserApiState(API_STATES.loading);

                let response = await CompanyApi.getAllUsers(selectedCompanyIds);
                if (response.status === API_STATES.success) {
                    const users = response.data;
                    setUserList(transformUsers(users));
                    setUserApiState(API_STATES.success);
                } else {
                    setUserApiError(response.error);
                    setUserList([]);
                    setUserApiState(API_STATES.error);
                }
            }
        } catch (e) {
            setUserApiError(e);
            setUserList([]);
            setUserApiState(API_STATES.error);
        }
    };

    useEffect(() => {
        if (selectedCompany?.length > 0 && isCompanyListChanged) {
            if (userFetchTimeoutRef.current) {
                clearTimeout(userFetchTimeoutRef.current);
            }
            userFetchTimeoutRef.current = setTimeout(() => {
                userSelectClicked();
            }, 300); // debounce 300ms to prevent rapid API calls
        }
        
        return () => {
            if (userFetchTimeoutRef.current) {
                clearTimeout(userFetchTimeoutRef.current);
            }
        };
    }, [selectedCompany, selectedCompanyIds, isCompanyListChanged]);

    const clearSelections = () => {
        setSelectedCompany([]);
        setSelectedUsers([]);
        setEndDate(null);
        setSelectedNotificationType([]);
        setNotificationHeader('');
        setNotificationLink('');
        setNotificationMessage('');
        setSkippedCompanies([]);
        setSkippedUsers([]);
    };

    async function createRequestBody() {
        const expirationDate = endDate ? moment(endDate).endOf('day').format('YYYY/MM/DD HH:mm:ss') : null;
        const messageType =
            selectedNotificationType?.value ??
            selectedNotificationType?.[0]?.value ??
            selectedNotification?.notificationType ??
            Constant.TYPE_OF_NOTIFICATION[1].value;

        if (edit && !editSingleNotification) {
            return {
                notificationId: selectedNotification.notificationId,
                expirationDtm: expirationDate,
                message: { message: notificationMessage, header: notificationHeader, hyperLink: notificationLink },
                messageType: messageType,
                triggerType: Constant.MANUAL,
                priority: Constant.PRIORITY.LOW,
                messageCreationType: Constant.MESSAGE_TYPE.USER_BASED,
            };
        } else if (edit && editSingleNotification) {
            return {
                id: selectedNotification.id,
                recepientId: selectedNotification.recepientId,
                notificationId: selectedNotification.notificationId,
                expirationDtm: expirationDate,
                message: { message: notificationMessage, header: notificationHeader, hyperLink: notificationLink },
                messageType: messageType,
                triggerType: Constant.MANUAL,
                priority: Constant.PRIORITY.LOW,
                messageCreationType: Constant.MESSAGE_TYPE.USER_BASED,
            };
        } else {
            return {
                selectedUserIds: selectedUsers[0] === Constant.ALL 
                    ? userList.map((obj) => obj.value.id)
                    : selectedUsers.map((obj) => obj.id),
                selectedCompanyIds: selectedCompanyIds,
                expirationDtm: expirationDate,
                message: { message: notificationMessage, header: notificationHeader, hyperLink: notificationLink },
                messageType: selectedNotificationType.value,
                triggerType: Constant.MANUAL,
                priority: Constant.PRIORITY.LOW,
                messageCreationType: Constant.MESSAGE_TYPE.USER_BASED,
            };
        }
    }

    async function createScheduledNotificationRequestBody(scheduledDtm) {
        const expirationDtm = endDate ? moment(endDate).endOf('day').format('YYYY/MM/DD HH:mm:ss') : null;

        const criteria = {
            selectedByzUserIds: selectedUsers[0] === Constant.ALL
                ? userList.map((obj) => obj.value.id)
                : selectedUsers.map((obj) => obj.id),
            selectedCompanyIds: selectedCompanyIds,
        }
        const messageDetails = {
            message: notificationMessage,
            header: notificationHeader,
            hyperLink: notificationLink,
            messageType: selectedNotificationType.value,
            priority: Constant.PRIORITY.LOW
        }

        return {
            scheduledDtm,
            expirationDtm,
            messageDetails,
            criteria
        };
    }

    const sendNotification = async () => {
        try {
            setOpenModal(false);

            clearSelections();
            setCreateApiState(API_STATES.loading);
            let requestBody = await createRequestBody();
            let response;
            if (edit && !editSingleNotification) {
                response = await MessageService.editAllMessages(requestBody);
            } else if (edit && editSingleNotification) {
                response = await MessageService.editSingleUserMessage(requestBody);
            } else {
                response = await MessageService.createConsumerMessage(requestBody);
            }
            if (response.status === API_STATES.success) {
                setCreateApiState(API_STATES.success);
                refresh(true);
                if (edit) {
                    closeEdit();
                }
            } else {
                setCreateApiError(response.error);
                setCreateApiState(API_STATES.error);
            }
        } catch (e) {
            setCreateApiError(e);
            setCreateApiState(API_STATES.error);
        }
    };
    const onSubmitScheduledNotification = async (time) => {
        try {
            setOpenScheduleNotificationModal(false);

            clearSelections();
            setCreateApiState(API_STATES.loading);
            const scheduledDtm = time.toDate().toISOString()
            let requestBody = await createScheduledNotificationRequestBody(scheduledDtm);
            console.log("requestBody", requestBody)
            const response = await MessageService.scheduleUserBasedNotification(requestBody);
            if (response.status === API_STATES.success) {
                setCreateApiState(API_STATES.success);
                refresh(true);
            } else {
                handleApiError(response.error, setCreateApiState, setCreateApiError)
            }
        } catch (e) {
            console.error('unexpected error in SubmitScheduledNotification', e)
            handleApiError(422, setCreateApiState, setCreateApiError)
        }
    };

    const displayPreview = () => {
        setOpenModal(true);
    };

    const validateSelection = () => {
        if (edit) {
            return !(
                selectedNotificationType?.length !== 0 &&
                notificationHeader &&
                notificationHeader?.trim() !== '' &&
                notificationMessage &&
                notificationMessage?.trim() !== '' &&
                endDate
            );
        } else {
            return !(
                selectedCompany?.length !== 0 &&
                selectedUsers?.length !== 0 &&
                selectedNotificationType?.length !== 0 &&
                notificationHeader &&
                notificationHeader.trim() !== '' &&
                notificationMessage &&
                notificationMessage.trim() !== '' &&
                endDate
            );
        }
    };

    const companyInputChange = (inputValue, actionMeta) => {
        if (actionMeta?.action === 'input-change' && companyList?.length !== 0 && inputValue.indexOf('|') !== -1) {
            const companies = inputValue.split('|');
            let res = companyList.filter((item) => companies.includes(item.display ?? item.label));
            let companyNames = res.map(({ display, label }) => display ?? label);
            let skippedValues = companies.filter((item) => !companyNames.includes(item));

            if (res?.length !== 0) {
                let list = selectedCompany;
                for (let value of res) {
                    if (!selectedCompany?.includes(value)) {
                        list.push(value);
                    }
                }
                if (list?.length !== 0) {
                    selectCompany(list);
                }
                let skippedCompaniesList = skippedCompanies;
                for (let value of skippedValues) {
                    if (!skippedCompanies?.includes(value)) {
                        skippedCompaniesList.push(value);
                    }
                }
                if (skippedCompaniesList?.length !== 0) {
                    setSkippedCompanies(skippedCompaniesList);
                }
            }
        }
    };

    const timePickerRef = useRef(null); // To detect outer click
    const userFetchTimeoutRef = useRef(null); // To debounce user fetch

    const handleClickOutsideTimePicker = useCallback(
        (event) => {
            if (timePickerRef.current && !timePickerRef.current.contains(event.target)) {
                setShowTimePicker(false);
            }
        },
        [timePickerRef]
    );

    useEffect(() => {
        // Bind the event listener for click on outside time picker
        if (showTimePicker) {
            document.addEventListener('mousedown', handleClickOutsideTimePicker);
        } else {
            document.removeEventListener('mousedown', handleClickOutsideTimePicker);
        }
    }, [handleClickOutsideTimePicker, showTimePicker]);

    const onScheduleNotification = async() =>{
        setOpenScheduleNotificationModal(true);
    }

    const updateScheduleDate=(time, newDate)=>{
        const dateTimeString = newDate + time.format(' hh:mm a');
        setTime(moment(dateTimeString, 'ddd MMM DD YYYY hh:mm a'));
    }

    const updateScheduleTime=(time, newTime)=> {
        console.log("newTime-->", newTime.formatted12)
        const dateTimeString = time.format('DD/MM/YYYY ') + newTime.formatted12;
        setTime(moment(dateTimeString, 'DD/MM/YYYY hh:mm a'));
    };

    const handleCompanyChange = useCallback((event) => {
        if (!event.value) {
            selectCompany([]);
            return;
        }
        const selectedOptions = Array.isArray(event.value) ? event.value : [event.value];

        selectCompany(selectedOptions);
    }, [selectCompany]);

    const handleUserChange = useCallback((event) => {
        if (!event.value) {
            selectUsers([]);
            return;
        }
        const selectedOptions = Array.isArray(event.value) ? event.value : [event.value];
        selectUsers(selectedOptions);
    }, [selectUsers]);

    const buttonLabel = useState(edit ? 'Edit' : 'Submit');
    return (
        <>
            {!edit && (
                <>
                    {' '}
                    <h1>User Based Notification</h1>
                    <hr />
                    {/* Company list fetch status */}
                    {companyApiState === API_STATES.loading && (
                        <span className={generalStyles.statusMessage}>Fetching Companies...</span>
                    )}
                    {companyApiState === API_STATES.error && (
                        <span className={generalStyles.errorMessage}>Failed: {companyApiError}</span>
                    )}
                    {/* User list fetch status */}
                    {userApiState === API_STATES.loading && (
                        <span className={generalStyles.statusMessage}>Fetching Users...</span>
                    )}
                    {userApiState === API_STATES.error && (
                        <span className={generalStyles.errorMessage}>Failed: {userApiError}</span>
                    )}
                    <br />
                </>
            )}

            {edit && (
                <>
                    {' '}
                    <h1>Edit User Based Notification</h1>
                    <hr />
                    <br />
                </>
            )}

            {(companyList.length > 0 || edit) && (
                <>
                    <div className={styles.selectionContainer}>
                        {/* Company select */}
                        <div className={styles.selectInputWrapper}>
                            <label className={styles.formFieldLabel}>Select Companies:</label>
                            <span className={styles.formField}>
                                <ByzzerWindowedSelect
                                    className={styles.reactSelect}
                                    options={companyList}
                                    value={selectedCompany}
                                    onChange={handleCompanyChange}
                                    allowClear={true}
                                    allowMultiple={true}
                                    disabled={edit}
                                    placeholder={'Companies'}
                                    showAllOption={true}
                                    loadingMessage={loadingCompanyListMessage}
                                    isLoading={companyApiState === API_STATES.loading}
                                    windowThreshold={100}
                                />
                            </span>
                        </div>

                        {/* User select */}
                        <div className={styles.selectInputWrapper}>
                            <label className={styles.formFieldLabel}>Select Users:</label>
                            <span className={styles.formField}>
                                <ByzzerWindowedSelect
                                    className={styles.reactSelect}
                                    options={userList}
                                    value={selectedUsers}
                                    onChange={handleUserChange}
                                    allowClear={true}
                                    allowMultiple={true}
                                    disabled={edit || selectedCompany?.length === 0}
                                    placeholder={'Users'}
                                    showAllOption={true}
                                    loadingMessage={loadingUserListMessage}
                                    isLoading={userApiState === API_STATES.loading}
                                    windowThreshold={100}
                                />
                            </span>
                        </div>

                        {/* Message expiration date selection */}
                        <div className={styles.selectInputWrapper}>
                            <label className={styles.formLabel}>Select Expiration Date:</label>

                            <div className={styles.datepickerContainer}>
                                <DatePicker
                                    name="endDate"
                                    placeholderText="End Date"
                                    shouldCloseOnSelect={true}
                                    className={styles.datepickerInput}
                                    fixedHeight
                                    closeOnScroll={true}
                                    dateFormat="MMM d, y"
                                    selected={endDate ? endDate.toDate() : null}
                                    peekNextMonth
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    tabIndex={1}
                                    onChange={(date) => {
                                        onDateChange(date ? moment(date) : null);
                                    }}
                                    minDate={moment().toDate()}
                                    onKeyDown={(e) => {
                                        e.preventDefault();
                                    }}
                                />

                                <img src={calendarIcon} alt="calendar icon" />
                            </div>
                        </div>
                    </div>

                    <br />

                    <div className={styles.selectionContainer}>
                        {/* Notification type selection */}
                        <div className={styles.selectInputWrapper}>
                            <label className={styles.formLabel}>Notification Type:</label>
                            <span className={styles.formField}>
                                <Select
                                    classNamePrefix={'react-select'}
                                    options={Constant.TYPE_OF_NOTIFICATION}
                                    onChange={handleSelectedNotificationType}
                                    value={selectedNotificationType}
                                    placeholder={'Notification Type'}
                                />
                            </span>
                        </div>

                        {/* input for message header/title */}
                        <div className={styles.selectInputWrapper}>
                            <label className={styles.formLabel}>Notification Header:</label>
                            <span className={styles.formField}>
                                <textarea
                                    onChange={(e) => handleHeaderChange(e)}
                                    value={notificationHeader}
                                    className={generalStyles.textareaInput}
                                    placeholder={'Notification Header'}
                                />
                            </span>
                        </div>

                        {/* input for message text */}
                        <div className={styles.selectInputWrapper}>
                            <label className={styles.formLabel}>Message:</label>
                            <span className={styles.formField}>
                                <textarea
                                    onChange={(e) => handleMessageChange(e)}
                                    value={notificationMessage}
                                    className={generalStyles.textareaInput}
                                    placeholder={'Notification Message'}
                                />
                            </span>
                        </div>
                    </div>

                    <br />

                    <div className={styles.selectionContainer}>
                        {/* input for adding URL for performing any action on the message */}
                        <div className={styles.selectInputWrapper}>
                            <label className={styles.formLabel}>Action:</label>
                            <span className={styles.formField}>
                                <textarea
                                    onChange={(e) => handleLinkChange(e)}
                                    value={notificationLink}
                                    className={generalStyles.textareaInput}
                                    placeholder={'Link (Optional) - Link for redirection'}
                                />
                            </span>
                        </div>

                        {/* button to submit all form selections to generate the message and send notification to selected users */}
                        <div>
                            <button
                                className={`${generalStyles.submitButton}`}
                                name={'submit'}
                                onClick={displayPreview}
                                disabled={validateSelection()}
                            >
                                Submit
                            </button>
                            {!edit && 
                                <button
                                    className={`${generalStyles.submitButton}`}
                                    name={'submit'}
                                    onClick={onScheduleNotification}
                                    disabled={validateSelection()}
                                >
                                Schedule
                                </button>
                            }
                            <div>
                                {createApiState === API_STATES.loading && (
                                    <span className={generalStyles.statusMessage}>
                                        Triggering User Based Notification...
                                    </span>
                                )}
                                {createApiState === API_STATES.error && (
                                    <span className={generalStyles.errorMessage}>Failed: {createApiError}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <Popup
                        open={openModal}
                        onClose={() => {
                            setOpenModal(false);
                        }}
                        modal
                    >
                        {() => (
                            <div className={styles.warningDialog}>
                                <h2>Preview</h2>
                                <div className={styles.messageContainer}>
                                    <div>
                                        {' '}
                                        <b>{notificationHeader}</b>: {notificationMessage}
                                    </div>
                                    <br></br>

                                    {notificationLink !== undefined &&
                                        notificationLink !== null &&
                                        notificationLink.trim() !== '' && (
                                            <>
                                                <div>
                                                    <b>Link</b>: {notificationLink}
                                                </div>
                                                <br></br>
                                            </>
                                        )}
                                    {!edit && (
                                        <div>
                                            <b>Note</b>: This message will be sent to {selectedUsersCount} accounts
                                        </div>
                                    )}

                                    {edit && !editSingleNotification && (
                                        <div>
                                            <b>Note</b>: This Message will be modified for all notified users.
                                        </div>
                                    )}
                                    {edit && editSingleNotification && (
                                        <div>
                                            <b>Note</b>: This Message will be modified for selected single user.
                                        </div>
                                    )}
                                </div>

                                {(skippedCompanies?.length !== 0 || skippedUsers?.length !== 0) && (
                                    <>
                                        {' '}
                                        <div>
                                            <b>Warning</b>:
                                            {skippedCompanies?.length !== 0 && (
                                                <span>
                                                    Companies <b>{skippedCompanies.join(', ')} </b>
                                                </span>
                                            )}
                                            {skippedCompanies?.length !== 0 && skippedUsers?.length !== 0 && (
                                                <span>and </span>
                                            )}
                                            {skippedUsers?.length !== 0 && (
                                                <span>
                                                    Users <b>{skippedUsers.join(', ')} </b>
                                                </span>
                                            )}
                                            will not be notified.
                                        </div>
                                        <br />
                                    </>
                                )}

                                <div>
                                    <button
                                        className={`${generalStyles.cancelOperationButton} ${styles.btnWidth}`}
                                        onClick={() => {
                                            sendNotification();
                                        }}
                                    >
                                        {buttonLabel}
                                    </button>

                                    <button
                                        className={`${generalStyles.confirmOperationButton} ${styles.btnWidth}`}
                                        onClick={() => {
                                            setOpenModal(false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </Popup>
                    <Popup
                        open={openScheduleNotificationModal}
                        onClose={() => {
                            setOpenScheduleNotificationModal(false);
                        }}
                        modal
                    >
                        {() => (
                            <div className={styles.warningDialog}>
                                <h2>Preview</h2>
                                <div className={styles.messageContainer}>
                                    <div>
                                        {' '}
                                        <b>{notificationHeader}</b>: {notificationMessage}
                                    </div>
                                    <br></br>

                                    {notificationLink !== undefined &&
                                        notificationLink !== null &&
                                        notificationLink.trim() !== '' && (
                                            <>
                                                <div>
                                                    <b>Link</b>: {notificationLink}
                                                </div>
                                                <br></br>
                                            </>
                                        )}
                                    {!edit && (
                                        <div>
                                            <b>Note</b>: This message will be sent to {selectedUsersCount} accounts
                                        </div>
                                    )}
                                    <div className={styles.etaForm}>
                                        <b>Select Date & Time: </b>
                                        <div className={styles.date}>
                                            <DatePicker
                                                data-test="schedule-notification-datePicker"
                                                name="scheduleDate"
                                                placeholderText="dd/MM/yyyy"
                                                shouldCloseOnSelect={true}
                                                fixedHeight
                                                closeOnScroll={true}
                                                minDate={new Date()}
                                                dateFormat="MMM d, y"
                                                selected={time.toDate()}
                                                peekNextMonth
                                                showMonthDropdown
                                                showYearDropdown
                                                dropdownMode="select"
                                                tabIndex={1}
                                                onChange={(newDate) => {
                                                    updateScheduleDate(time, newDate);
                                                }}
                                            />
                                            <img src={calendarIcon} alt="calendar icon" />
                                        </div>
                                        <div className={styles.time}>
                                            <div className={styles.timePicker} ref={timePickerRef}>
                                                <div className={styles.timeDisplay} onClick={() => setShowTimePicker(!showTimePicker)}>
                                                    <input value={time.format('hh:mm a')} />
                                                    <img src={clockIcon} alt="clock icon" />
                                                </div>
                                                {showTimePicker && (
                                                    <div className={styles.timePickerElementWrapper}>
                                                        <TimeKeeper
                                                            data-test="maintenance-timePicker"
                                                            time={time.format('hh:mm a')}
                                                            onChange={(newTime) => updateScheduleTime(time, newTime)}
                                                            onDoneClick={() => setShowTimePicker(false)}
                                                            switchToMinuteOnHourSelect
                                                            closeOnMinuteSelect
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {(skippedCompanies?.length !== 0 || skippedUsers?.length !== 0) && (
                                    <>
                                        {' '}
                                        <div>
                                            <b>Warning</b>:
                                            {skippedCompanies?.length !== 0 && (
                                                <span>
                                                    Companies <b>{skippedCompanies.join(', ')} </b>
                                                </span>
                                            )}
                                            {skippedCompanies?.length !== 0 && skippedUsers?.length !== 0 && (
                                                <span>and </span>
                                            )}
                                            {skippedUsers?.length !== 0 && (
                                                <span>
                                                    Users <b>{skippedUsers.join(', ')} </b>
                                                </span>
                                            )}
                                            will not be notified.
                                        </div>
                                        
                                    </>
                                )}

                                <div>
                                    <button
                                        className={`${generalStyles.cancelOperationButton} ${styles.btnWidth}`}
                                        onClick={() => {
                                            onSubmitScheduledNotification(time);
                                        }}
                                        disabled={!time || scheduledDate?.isSame(time) || time < moment() || updateScheduledDateApiState !== API_STATES.none}
                                    >
                                        Schedule
                                    </button>

                                    <button
                                        className={`${generalStyles.confirmOperationButton} ${styles.btnWidth}`}
                                        onClick={() => {
                                            setOpenScheduleNotificationModal(false);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </Popup>
                </>
            )}
        </>
    );
}

export default UserBasedNotificationForm;
